import { ChatThread, ChatMessage } from '../../contexts/ChatContext';
import { ModelSelection } from '../../types/models';

export interface ThreadTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  initialMessage?: string;
  context?: Record<string, any>;
}

export interface ThreadSearchResult {
  thread: ChatThread;
  relevanceScore: number;
  matchedFields: string[];
}

export class ThreadManager {
  private static instance: ThreadManager;
  private threads: Map<string, ChatThread> = new Map();
  private pinnedArtifacts: Set<string> = new Set();
  private threadTemplates: ThreadTemplate[] = [];

  private constructor() {
    this.initializeTemplates();
    this.loadFromStorage();
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  public static getInstance(): ThreadManager {
    if (!ThreadManager.instance) {
      ThreadManager.instance = new ThreadManager();
    }
    return ThreadManager.instance;
  }

  private initializeTemplates() {
    this.threadTemplates = [
      {
        id: 'general',
        name: 'General Discussion',
        description: 'General conversation and questions',
        icon: '💬',
        initialMessage: 'Welcome! How can I help you today?',
      },
      {
        id: 'model-design',
        name: 'Model Design',
        description: 'Design and architecture discussions',
        icon: '🏗️',
        initialMessage: 'Let\'s work on your model design. What would you like to create or modify?',
      },
      {
        id: 'process-analysis',
        name: 'Process Analysis',
        description: 'Process analysis and optimization',
        icon: '⚙️',
        initialMessage: 'Let\'s analyze your processes. What process would you like to examine?',
      },
      {
        id: 'validation',
        name: 'Validation & Testing',
        description: 'Model validation and testing',
        icon: '✅',
        initialMessage: 'Let\'s validate your model. What would you like to check?',
      },
      {
        id: 'ai-assistant',
        name: 'AI Assistant',
        description: 'AI-powered assistance and suggestions',
        icon: '🤖',
        initialMessage: 'I\'m your AI assistant. How can I help you improve your model?',
      },
    ];
  }

  // Thread Management
  public createThread(name: string, description?: string, templateId?: string, model?: ModelSelection, uiRole?: string, context?: any): ChatThread {
    const id = this.generateUUID();
    const now = new Date().toISOString();
    
    let initialMessage: ChatMessage | undefined;
    if (templateId) {
      const template = this.threadTemplates.find(t => t.id === templateId);
      if (template?.initialMessage) {
        initialMessage = {
          id: this.generateUUID(),
          threadId: id,
          who: 'assistant',
          text: template.initialMessage,
          timestamp: now,
          type: 'text',
        };
      }
    }

    const thread: ChatThread = {
      id,
      name,
      description,
      createdAt: now,
      updatedAt: now,
      messageCount: initialMessage ? 1 : 0,
      isPinned: false,
      tags: templateId ? [templateId] : [],
      participants: [],
      lastMessage: initialMessage,
      model: model || { type: 'company' as any },
      uiRole: uiRole || 'architect',
      context: context || {
        selectedArtifactId: undefined,
        environment: 'Company',
      },
    };

    this.threads.set(id, thread);
    this.saveToStorage();
    
    return thread;
  }

  public getThread(threadId: string): ChatThread | undefined {
    return this.threads.get(threadId);
  }

  public getAllThreads(): ChatThread[] {
    return Array.from(this.threads.values()).sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }

  public updateThread(threadId: string, updates: Partial<ChatThread>): boolean {
    const thread = this.threads.get(threadId);
    if (!thread) return false;

    const updatedThread = {
      ...thread,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    this.threads.set(threadId, updatedThread);
    this.saveToStorage();
    return true;
  }

  public deleteThread(threadId: string): boolean {
    const deleted = this.threads.delete(threadId);
    if (deleted) {
      this.saveToStorage();
    }
    return deleted;
  }

  public pinThread(threadId: string): boolean {
    return this.updateThread(threadId, { isPinned: true });
  }

  public unpinThread(threadId: string): boolean {
    return this.updateThread(threadId, { isPinned: false });
  }

  public searchThreads(query: string): ThreadSearchResult[] {
    const lowerQuery = query.toLowerCase();
    const results: ThreadSearchResult[] = [];

    this.threads.forEach(thread => {
      let relevanceScore = 0;
      const matchedFields: string[] = [];

      // Search in name
      if (thread.name.toLowerCase().includes(lowerQuery)) {
        relevanceScore += 10;
        matchedFields.push('name');
      }

      // Search in description
      if (thread.description?.toLowerCase().includes(lowerQuery)) {
        relevanceScore += 5;
        matchedFields.push('description');
      }

      // Search in tags
      if (thread.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) {
        relevanceScore += 3;
        matchedFields.push('tags');
      }

      // Search in last message
      if (thread.lastMessage?.text?.toLowerCase().includes(lowerQuery)) {
        relevanceScore += 2;
        matchedFields.push('lastMessage');
      }

      if (relevanceScore > 0) {
        results.push({
          thread,
          relevanceScore,
          matchedFields,
        });
      }
    });

    return results.sort((a, b) => b.relevanceScore - a.relevanceScore);
  }

  // Message Management
  public addMessage(threadId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>): ChatMessage | null {
    const thread = this.threads.get(threadId);
    if (!thread) return null;

    const newMessage: ChatMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
    };

    const updatedThread = {
      ...thread,
      messageCount: thread.messageCount + 1,
      lastMessage: newMessage,
      updatedAt: new Date().toISOString(),
    };

    this.threads.set(threadId, updatedThread);
    this.saveToStorage();
    
    return newMessage;
  }

  public getMessages(threadId: string): ChatMessage[] {
    // In a real implementation, this would fetch from a separate message store
    // For now, we'll return the last message if it exists
    const thread = this.threads.get(threadId);
    return thread?.lastMessage ? [thread.lastMessage] : [];
  }

  // Artifact Pinning
  public pinArtifact(artifactId: string): boolean {
    this.pinnedArtifacts.add(artifactId);
    this.saveToStorage();
    return true;
  }

  public unpinArtifact(artifactId: string): boolean {
    const removed = this.pinnedArtifacts.delete(artifactId);
    if (removed) {
      this.saveToStorage();
    }
    return removed;
  }

  public getPinnedArtifacts(): string[] {
    return Array.from(this.pinnedArtifacts);
  }

  public isArtifactPinned(artifactId: string): boolean {
    return this.pinnedArtifacts.has(artifactId);
  }

  // Templates
  public getTemplates(): ThreadTemplate[] {
    return this.threadTemplates;
  }

  public getTemplate(templateId: string): ThreadTemplate | undefined {
    return this.threadTemplates.find(t => t.id === templateId);
  }

  // Context Management
  public updateThreadContext(threadId: string, context: Record<string, any>): boolean {
    const thread = this.threads.get(threadId);
    if (!thread) return false;

    const updatedThread = {
      ...thread,
      context: {
        ...thread.context,
        ...context,
      },
      updatedAt: new Date().toISOString(),
    };

    this.threads.set(threadId, updatedThread);
    this.saveToStorage();
    return true;
  }

  public getThreadContext(threadId: string): Record<string, any> | undefined {
    const thread = this.threads.get(threadId);
    return thread?.context;
  }

  // Storage
  private saveToStorage(): void {
    try {
      const data = {
        threads: Array.from(this.threads.entries()),
        pinnedArtifacts: Array.from(this.pinnedArtifacts),
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('krysalis_threads', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save threads to storage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const data = localStorage.getItem('krysalis_threads');
      if (data) {
        const parsed = JSON.parse(data);
        
        // Load threads
        if (parsed.threads) {
          this.threads = new Map(parsed.threads);
        }
        
        // Load pinned artifacts
        if (parsed.pinnedArtifacts) {
          this.pinnedArtifacts = new Set(parsed.pinnedArtifacts);
        }
      }
    } catch (error) {
      console.error('Failed to load threads from storage:', error);
    }
  }

  // Statistics
  public getStatistics(): {
    totalThreads: number;
    pinnedThreads: number;
    totalMessages: number;
    pinnedArtifacts: number;
  } {
    const threads = Array.from(this.threads.values());
    return {
      totalThreads: threads.length,
      pinnedThreads: threads.filter(t => t.isPinned).length,
      totalMessages: threads.reduce((sum, t) => sum + t.messageCount, 0),
      pinnedArtifacts: this.pinnedArtifacts.size,
    };
  }

  // Cleanup
  public cleanup(): void {
    // Remove old threads (older than 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let cleaned = 0;
    this.threads.forEach((thread, id) => {
      if (new Date(thread.updatedAt) < thirtyDaysAgo && !thread.isPinned) {
        this.threads.delete(id);
        cleaned++;
      }
    });

    if (cleaned > 0) {
      this.saveToStorage();
      console.log(`Cleaned up ${cleaned} old threads`);
    }
  }
}
